import React, { useContext, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { DatePicker } from 'baseui/datepicker';
import { Drawer } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input } from 'baseui/input';
import { ListItem } from 'baseui/list';
import { Pagination } from 'baseui/pagination';
import { Select } from 'baseui/select';
import { Skeleton } from 'baseui/skeleton';
import { faFilter, faInbox } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonRow, ScrollToTopButton } from '@gtintel/platform-atoms';
import { COLOURS, useDebounce } from '@gtintel/platform-utils';
import { AssignmentsContext, AssignmentsNavigationContext } from '../context';
import { OrderChoicesEnum } from '../types';
import AssignmentListItem from './AssignmentListItem';
const StatusChoices = [{
  label: 'Pending Acceptance',
  id: '1'
}, {
  label: 'Declined',
  id: '2'
}, {
  label: 'Awaiting Activation',
  id: '3'
}, {
  label: 'Activated',
  id: '5'
}, {
  label: 'Submitted',
  id: '7'
}, {
  label: 'Finished',
  id: '8'
}];
const OrderChoices = [{
  label: 'Updated Date',
  id: OrderChoicesEnum.UpdatedAt
}, {
  label: 'Updated Date (Descending)',
  id: OrderChoicesEnum.UpdatedAtDescending
}, {
  label: 'Created Date',
  id: OrderChoicesEnum.CreatedAt
}, {
  label: 'Created Date (Descending)',
  id: OrderChoicesEnum.CreatedAtDescending
}, {
  label: 'Submitted Date',
  id: OrderChoicesEnum.SubmittedAt
}, {
  label: 'Submitted Date (Descending)',
  id: OrderChoicesEnum.SubmittedAtDescending
}, {
  label: 'Due Date',
  id: OrderChoicesEnum.DueDate
}, {
  label: 'Due Date (Descending)',
  id: OrderChoicesEnum.DueDateDescending
}, {
  label: 'Name',
  id: OrderChoicesEnum.Name
}, {
  label: 'Name (Descending)',
  id: OrderChoicesEnum.NameDescending
}, {
  label: 'Project Name',
  id: OrderChoicesEnum.ProjectName
}, {
  label: 'Project Name (Descending)',
  id: OrderChoicesEnum.ProjectNameDescending
}];

// Order the assignments based on the order choice
const orderAssignments = (assignments, orderTerm) => {
  if (!orderTerm) {
    return assignments;
  }
  switch (orderTerm.id) {
    case OrderChoicesEnum.UpdatedAt:
      return assignments.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    case OrderChoicesEnum.UpdatedAtDescending:
      return assignments.sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
    case OrderChoicesEnum.CreatedAt:
      return assignments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    case OrderChoicesEnum.CreatedAtDescending:
      return assignments.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    case OrderChoicesEnum.SubmittedAt:
      return assignments.sort((a, b) => new Date(b.submitted_date).getTime() - new Date(a.submitted_date).getTime());
    case OrderChoicesEnum.SubmittedAtDescending:
      return assignments.sort((a, b) => new Date(a.submitted_date).getTime() - new Date(b.submitted_date).getTime());
    case OrderChoicesEnum.DueDate:
      return assignments.sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime());
    case OrderChoicesEnum.DueDateDescending:
      return assignments.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
    case OrderChoicesEnum.Name:
      return assignments.sort((a, b) => a.name.localeCompare(b.name));
    case OrderChoicesEnum.NameDescending:
      return assignments.sort((a, b) => b.name.localeCompare(a.name));
    case OrderChoicesEnum.ProjectName:
      return assignments.sort((a, b) => a.project.name.localeCompare(b.project.name));
    case OrderChoicesEnum.ProjectNameDescending:
      return assignments.sort((a, b) => b.project.name.localeCompare(a.project.name));
    default:
      return assignments;
  }
};
const AssignmentList = () => {
  const navigation = React.useContext(AssignmentsNavigationContext);
  const [isLoading, setIsLoading] = useState(false);
  const [css] = useStyletron();
  const {
    filter,
    list,
    searchTerm,
    editSearchTerm,
    orderTerm,
    setOrderTerm,
    filterStatuses,
    setFilterDateAfter,
    setFilterDateBefore,
    currentPage,
    totalPages,
    getNext,
    getPrev,
    search
  } = useContext(AssignmentsContext);
  const [term, setTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [page, setPage] = useState(1);
  const [dateRangeValue, setDateRangeValue] = React.useState([]);
  const [order, setOrder] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const paginate = page => {
    setIsLoading(true);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    if (page < Number(currentPage())) {
      getPrev();
    } else {
      getNext();
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  // Setting these in the declarations above caused some of them to sometimes be empty but directly setting them here in
  // the useEffect call stops that. Still not 100% sure why, likely a rendering order problem.
  useEffect(() => {
    setStatusFilters(filterStatuses());
    setPage(currentPage());
    setOrder(orderTerm());
    setTerm(searchTerm());
  }, []);
  const debouncedSearch = useDebounce(search, 500);
  const assignments = orderAssignments(list(), orderTerm());
  const setSearchFilter = searchTerm => {
    localStorage.setItem('searchTerm', searchTerm);
    setTerm(searchTerm);
    editSearchTerm(searchTerm);
  };
  const setStatusFilter = statuses => {
    localStorage.setItem('filterStatuses', JSON.stringify(statuses));
    setStatusFilters(statuses);
  };
  const setDateFilter = dateRange => {
    if (dateRange.length === 0) {
      localStorage.removeItem('dateAfter');
      localStorage.removeItem('dateBefore');
      setDateRangeValue(dateRange);
      setFilterDateAfter(null);
      setFilterDateBefore(null);
      return;
    }
    localStorage.setItem('dateAfter', dateRange[0].toISOString());
    dateRange.length > 1 && localStorage.setItem('dateBefore', dateRange[1].toISOString());
    setDateRangeValue(dateRange);
    setFilterDateAfter(dateRange[0]);
    dateRange.length > 1 && setFilterDateBefore(dateRange[1]);
  };
  const setOrderFilter = id => {
    localStorage.setItem('order', JSON.stringify(OrderChoices.find(o => o.id === id) || OrderChoices[0]));
    setOrder(OrderChoices.find(o => o.id === id) || OrderChoices[0]);
  };
  const clearFilters = () => {
    localStorage.removeItem('searchTerm');
    localStorage.removeItem('filterStatuses');
    localStorage.removeItem('order');
    setSearchFilter('');
    setStatusFilter([]);
    setDateFilter([]);
    doFiltering();
  };
  const doFiltering = () => {
    setOrderTerm(order);
    filter();
  };
  return <section className={css({
    display: 'flex',
    flexFlow: 'column nowrap',
    width: '100%'
  })}>
      <HeadingLevel>
        <Heading>Assignments</Heading>
      </HeadingLevel>
      <div className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row no wrap',
      gap: '8px',
      marginBottom: '24px'
    })}>
        <Input value={term} onChange={e => {
        setIsLoading(true);
        setSearchFilter(e.target.value);
        debouncedSearch();
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }} overrides={{
        Root: {
          style: {
            margin: '0'
          }
        }
      }} placeholder="Search for assignment or project name..." />
        <Button testId="assignment-filter-button" onClick={() => setIsOpen(true)} kind="primary">
          <FontAwesomeIcon icon={faFilter} size="lg" className={css({
          color: 'white !important'
        })} />
        </Button>
      </div>

      {isLoading ? <section className={css({
      display: 'flex',
      flexFlow: 'column wrap',
      padding: '14px 28px',
      width: '100%'
    })}>
          <Skeleton autoSizeRows animation rows={5} height="500px" width="100%" />
        </section> : assignments.length > 0 ? assignments.map(a => {
      const status = a.status;
      return <ListItem onClick={() => navigation.navigateTo(a.id)} key={a.id} overrides={{
        Root: {
          style: {
            borderLeft: a.is_lead_partner_assignment || a.is_qa_assignment ? `5px solid ${a.is_lead_partner_assignment ? COLOURS.gold : COLOURS.blue}` : '5px solid transparent'
          }
        },
        Content: {
          style: {
            marginLeft: '0',
            paddingLeft: '16px'
          }
        }
      }}>
              <AssignmentListItem assignment={a} status={status} />
            </ListItem>;
    }) : <article className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'center',
      margin: '0 auto',
      height: 'calc(100vh - 286px)',
      width: '100%'
    })}>
          <FontAwesomeIcon icon={faInbox} size="6x" className={css({
        color: 'black !important'
      })} />
          <HeadingLevel>
            <Heading>No assignments at the moment</Heading>
          </HeadingLevel>
        </article>}

      <Pagination numPages={totalPages()} currentPage={page} onPageChange={({
      nextPage
    }) => {
      const page = Math.min(Math.max(nextPage, 1), totalPages());
      setPage(page);
      paginate(page);
    }} />

      <ScrollToTopButton />
      <Drawer isOpen={isOpen} autoFocus onClose={() => setIsOpen(false)} overrides={{
      Root: {
        props: {
          'data-testid': 'drawer'
        }
      },
      DrawerContainer: {
        style: {
          paddingTop: '64px'
        }
      },
      Close: {
        style: {
          top: '77px'
        }
      }
    }}>
        <div className={css({
        display: 'flex',
        flexFlow: 'column nowrap',
        gap: '8px',
        marginBottom: '14px'
      })}>
          <HeadingLevel>
            <Heading>Filters</Heading>
          </HeadingLevel>

          <div>
            <FormControl label={() => 'Last Updated'}>
              <DatePicker placeholder="Updated between.." value={dateRangeValue} onChange={({
              date
            }) => {
              setDateFilter(Array.isArray(date) ? date : [date]);
            }} range clearable quickSelect maxDate={new Date()} overrides={{
              Root: {
                props: {
                  'data-testid': 'date-picker'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Order By'}>
              <Select value={order ? [order] : [OrderChoices[0]]} disabled={isLoading} placeholder="Order By" onChange={params => {
              setOrderFilter(String(params.value[0].id));
            }} options={OrderChoices} clearable={false} overrides={{
              Root: {
                props: {
                  'data-testid': 'order-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Statuses'}>
              <Select value={statusFilters} disabled={isLoading} placeholder="Select 1 or more statuses" onChange={params => {
              const values = params.value.map(p => {
                return {
                  label: p.label.toString(),
                  id: String(p.id)
                };
              });
              setStatusFilter(values);
            }} options={StatusChoices} multi clearable overrides={{
              Root: {
                props: {
                  'data-testid': 'status-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <ButtonRow>
            <Button testId="apply-filters" onClick={() => {
            setIsOpen(false);
            setIsLoading(true);
            doFiltering();
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
          }}>
              Apply Filters
            </Button>
            <Button testId="clear-filters" onClick={() => {
            setIsOpen(false);
            clearFilters();
          }} kind="error">
              Clear Filters
            </Button>
          </ButtonRow>
        </div>
      </Drawer>
    </section>;
};
export default AssignmentList;